<script>
    import { createEventDispatcher } from "svelte";

    import SelectImage from "../../../nodes/SelectImage/v2/SelectImage.svelte";

    import { guidV4 } from "@itslearning/atlas/generate/guid";
    import { uploadFileToFileRepo } from "@itslearning/atlas/network/fileRepo";
    import { fileErrors, validateFile } from "@itslearning/atlas/files/validation";

    export let id = guidV4();

    /** @type {object} */
    export let fileRepoSettings;

    /** @type {object} */
    export let validationSettings = {
        invalidExtensions: undefined,
        allowedExtensions: undefined,
        invalidFileNameCharacters: undefined,
        maxFileNameLength: undefined,
        maxFileSizeBytes: undefined,
    };

    /** @type {string} */
    export let url = undefined;

    /** @type {boolean} */
    export let inProgress = false;

    /** @type {Boolean} */
    export let showLabel = true;

    export let i18n;

    /** @type {"image" | "avatar"} */
    export let previewType = "image";

    /** @type {string} */
    export let cssPreviewClass = "prom-select-image__preview";

    /** @type {Boolean}*/
    export let showImageDescriptionField= false;

    /** @type {string} */
    export let imageDescription = "";

    /** @type {number} */
    export let imageDescriptionMaxLength = 1024;

    /** @type {string} */
    export let errorMessage = undefined;

    $: allowedExtensions = validationSettings.allowedExtensions
        ? validationSettings.allowedExtensions.map(e => `.${e}`).join(",")
        : undefined;

    const dispatch = createEventDispatcher();

    async function changeFile(event) {
        const file = event.detail;

        let validation = validateFile(file, validationSettings);

        if (validation.valid && validationSettings.validate) {
            validation = validationSettings.validateLocalFile(file);
        }

        if (validation.valid) {
            await startFileRepoUpload(file);
        } else {
            handleUploadUserPictureError(validation.error);
        }
    }

    function removeFile() {
        dispatch("fileRemoved");
        errorMessage = "";
        url = undefined;
    }

    async function startFileRepoUpload(file) {
        inProgress = true;
        errorMessage = "";

        let error;

        try {
            const fileRepoFile = await uploadFileToFileRepo(file, fileRepoSettings);

            inProgress = false;

            if (!fileRepoFile.error) {
                fileRepoFile.url = URL.createObjectURL(file);
                dispatch("fileUploaded", fileRepoFile);
                url = fileRepoFile.url;
            } else {
                error = fileRepoFile.error;
            }
        } catch (err) {
            error = err;
            inProgress = false;
        }

        if (error) {
            handleUploadUserPictureError(fileErrors.unknown);
        }
    }

    function handleUploadUserPictureError(fileError) {
        switch (fileError) {
            case fileErrors.tooLarge:
                errorMessage = i18n.fileSizeExceededDescription;
                break;
            case fileErrors.tooSmall:
                errorMessage = i18n.uploadUserPictureTerms.fileTooSmall;
                break;
            case fileErrors.invalidExtension:
                errorMessage = i18n.uploadUserPictureTerms.invalidFileType;
                break;
            case fileErrors.invalidFileName:
                errorMessage = i18n.uploadUserPictureTerms.invalidFileNameCharacters;
                break;
            case fileErrors.nameTooLong:
                errorMessage = i18n.uploadUserPictureTerms.fileNameTooLong;
                break;
            default:
                errorMessage = i18n.uploadUserPictureTerms.uploadError;
                break;
        }
    }
</script>

<SelectImage {id}
    {url}
    {showLabel}
    {inProgress}
    {errorMessage}
    {i18n}
    {previewType}
    {cssPreviewClass}
    {allowedExtensions}
    {showImageDescriptionField}
    {imageDescriptionMaxLength}
    bind:imageDescription={imageDescription}
    on:change={changeFile}
    on:remove={removeFile}
/>
